import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import type { BoxProps } from "@mui/material/Box";
import type { BaseMoleculeProps } from "../../types";

export interface LoadingContainerProps extends BaseMoleculeProps {
  /**
   * Loading message to display
   */
  message?: string;

  /**
   * Size of the loading spinner
   */
  size?: number | "small" | "medium" | "large";

  /**
   * Whether to show as backdrop overlay
   */
  backdrop?: boolean;

  /**
   * Color of the loading spinner
   */
  color?: "primary" | "secondary" | "inherit";

  /**
   * Additional Box props for container styling
   */
  containerProps?: BoxProps;
}

const LoadingContainerComponent: React.FC<LoadingContainerProps> = ({
  message,
  size = "large",
  backdrop = false,
  color = "primary",
  loading = true,
  variant = "default",
  className,
  "data-testid": dataTestId,
  containerProps,
  children,
}) => {
  // Don't render if not loading
  if (!loading) {
    return <>{children}</>;
  }

  const loadingContent = (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight={backdrop ? "100vh" : variant === "modal" ? "300px" : "200px"}
      padding={2}
      className={className}
      data-testid={dataTestId}
      sx={{
        // Mobile-first responsive styling
        gap: {
          xs: 2, // Mobile spacing
          md: 3, // Desktop spacing
        },
        // Variant-specific styling
        ...(variant === "compact" && {
          minHeight: "100px",
          padding: 1,
        }),
        ...(variant === "expanded" && {
          minHeight: backdrop ? "100vh" : "400px",
          padding: 4,
        }),
        ...(variant === "modal" && {
          minHeight: "300px",
          maxHeight: "60vh",
          padding: { xs: 3, md: 4 },
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
        }),
      }}
      {...containerProps}
    >
      <CircularProgress
        size={
          typeof size === "string"
            ? size === "small"
              ? 24
              : size === "medium"
                ? 40
                : 56
            : size
        }
        color={color}
        sx={{
          // Mobile-first responsive sizing
          ...(typeof size === "string" && {
            width: {
              xs: size === "small" ? 24 : size === "medium" ? 32 : 40,
              md: size === "small" ? 24 : size === "medium" ? 40 : 56,
            },
            height: {
              xs: size === "small" ? 24 : size === "medium" ? 32 : 40,
              md: size === "small" ? 24 : size === "medium" ? 40 : 56,
            },
          }),
        }}
      />
      {message && (
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          sx={{
            // Mobile-first responsive typography
            fontSize: {
              xs: "0.875rem", // Mobile
              md: "1rem", // Desktop
            },
            maxWidth: {
              xs: "280px", // Mobile
              md: "400px", // Desktop
            },
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );

  // Render with backdrop if requested
  if (backdrop) {
    return (
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        {loadingContent}
      </Backdrop>
    );
  }

  return loadingContent;
};

// Apply memoization with React.memo
export const LoadingContainer = React.memo(LoadingContainerComponent);

// Set display name for debugging
(LoadingContainer as any).displayName = "LoadingContainer";
